import { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Container,
  Divider, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { useProject } from '../../hooks/ProjectContext';

export default function CapturePage() {
  const [searchParams] = useSearchParams();
  const { selectedProject, selectedSite } = useProject();

  const projectId = searchParams.get('projectId') || selectedProject?.code || '';
  const siteId = searchParams.get('siteId') || selectedSite?.code || '';
  const fieldRecordId = searchParams.get('fieldRecordId') || '';
  const mode = searchParams.get('mode') || (fieldRecordId ? 'sampling' : '');

  // Form state
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(mode === 'sampling' ? 'sampling' : 'general');
  const [notes, setNotes] = useState('');

  // Sampling points + field record
  const [samplingPoint, setSamplingPoint] = useState('');
  const [samplingPoints, setSamplingPoints] = useState([]);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [fieldRecord, setFieldRecord] = useState(null);
  const [activeFieldRecordId, setActiveFieldRecordId] = useState(fieldRecordId);
  const [availableFieldRecords, setAvailableFieldRecords] = useState([]);

  // Photo
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const cameraRef = useRef();
  const pickerRef = useRef();

  // GPS
  const [gps, setGps] = useState(null);
  const [gpsStatus, setGpsStatus] = useState('Acquiring GPS…');
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Distance + bearing to selected sampling point
  const proximity = useMemo(() => {
    if (!gps || !samplingPoint) return null;
    const pt = samplingPoints.find(p => p.name === samplingPoint);
    if (!pt) return null;
    const ptLat = parseFloat(pt.lat), ptLng = parseFloat(pt.lng);
    if (!isFinite(ptLat) || !isFinite(ptLng)) return null;

    const R = 6371000;
    const toRad = Math.PI / 180;
    const dLat = (ptLat - gps.lat) * toRad;
    const dLng = (ptLng - gps.lng) * toRad;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(gps.lat * toRad) * Math.cos(ptLat * toRad) * Math.sin(dLng / 2) ** 2;
    const distM = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const y = Math.sin((ptLng - gps.lng) * toRad) * Math.cos(ptLat * toRad);
    const x = Math.cos(gps.lat * toRad) * Math.sin(ptLat * toRad) - Math.sin(gps.lat * toRad) * Math.cos(ptLat * toRad) * Math.cos((ptLng - gps.lng) * toRad);
    const bearingDeg = (Math.atan2(y, x) * (180 / Math.PI) + 360) % 360;

    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const direction = dirs[Math.round(bearingDeg / 45) % 8];

    const distLabel = distM < 1000 ? `${Math.round(distM)} m` : `${(distM / 1000).toFixed(2)} km`;
    return { distM, distLabel, direction, bearingDeg: Math.round(bearingDeg) };
  }, [gps, samplingPoint, samplingPoints]);

  // Watch device GPS continuously so distance/bearing updates as user moves
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsStatus('Geolocation not available — enter coordinates manually');
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        setGps({ lat, lng, accuracyM: accuracy ?? null });
        setGpsStatus(`${lat.toFixed(5)}, ${lng.toFixed(5)}${accuracy ? ` (±${accuracy.toFixed(0)} m)` : ''}`);
      },
      () => setGpsStatus('GPS unavailable — enter coordinates manually'),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 5000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  const isSamplingMode = mode === 'sampling' || category === 'sampling';

  // Load available field records whenever sampling category is selected and no record pre-linked
  useEffect(() => {
    if (!isSamplingMode || activeFieldRecordId || !projectId) return;
    fetch(`/v1/field-records?project=${encodeURIComponent(projectId)}`)
      .then(r => r.ok ? r.json() : [])
      .then(rows => setAvailableFieldRecords(Array.isArray(rows) ? rows.filter(r => r.status === 'draft') : []))
      .catch(() => setAvailableFieldRecords([]));
  }, [projectId, isSamplingMode, activeFieldRecordId]);

  // Load sampling points from the active field record
  useEffect(() => {
    if (!isSamplingMode) return;
    if (!activeFieldRecordId) { setSamplingPoints([]); return; }
    setLoadingPoints(true);
    fetch(`/v1/field-records/${activeFieldRecordId}`)
      .then(r => r.ok ? r.json() : null)
      .then(rec => {
        if (!rec) return;
        setFieldRecord(rec);
        setSamplingPoints((rec.points || []).map(p => ({
          id: p.id,
          name: p.planned_name,
          lat: p.planned_lat,
          lng: p.planned_lng,
          status: p.status,
        })));
      })
      .catch(() => setSamplingPoints([]))
      .finally(() => setLoadingPoints(false));
  }, [activeFieldRecordId, mode]);

  // Auto-select nearest sampling point once GPS arrives
  useEffect(() => {
    if (!gps || samplingPoints.length === 0 || samplingPoint) return;
    let best = null, bestDist = Infinity;
    for (const pt of samplingPoints) {
      const lat = parseFloat(pt.lat), lng = parseFloat(pt.lng);
      if (!isFinite(lat) || !isFinite(lng)) continue;
      const d = Math.hypot(lat - gps.lat, lng - gps.lng);
      if (d < bestDist) { bestDist = d; best = pt; }
    }
    // ~200 m threshold (≈ 0.002°)
    if (best && bestDist < 0.002) setSamplingPoint(best.name);
  }, [gps, samplingPoints, samplingPoint]);

  function handleFileChange(f) {
    if (!f) return;
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  }

  async function handleSubmit() {
    setResult(null);
    if (!projectId) return setResult({ ok: false, message: 'Select a project from the header.' });
    if (!title.trim()) return setResult({ ok: false, message: 'Title is required.' });
    if (!file) return setResult({ ok: false, message: 'Choose a photo first.' });
    if (isSamplingMode && samplingPoints.length > 0 && !samplingPoint) {
      return setResult({ ok: false, message: 'Select a sampling point.' });
    }

    const lat = gps?.lat ?? parseFloat(manualLat);
    const lng = gps?.lng ?? parseFloat(manualLng);
    if (!isFinite(lat) || !isFinite(lng)) {
      return setResult({ ok: false, message: 'Location required. Enable GPS or enter coordinates manually.' });
    }

    setSubmitting(true);
    try {
      const initRes = await fetch('/v1/evidence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId,
          siteId: siteId || undefined,
          title: title.trim(),
          category,
          notes: notes.trim() || undefined,
          samplingPoint: samplingPoint || undefined,
          fieldRecordId: activeFieldRecordId ? Number(activeFieldRecordId) : undefined,
          capturedAt: new Date().toISOString(),
          lat, lng,
          accuracyM: gps?.accuracyM ?? null,
          contentType: file.type || 'image/jpeg',
          fileSize: file.size,
          source: { gps: gps ? 'device' : 'manual', time: 'device' },
        }),
      });
      const init = await initRes.json();
      if (!init.ok) throw new Error(init.message || 'Create failed');
      // Per-item capability returned by create — required by upload/complete since
      // the evidence mutation endpoints stopped being public.
      const cap = init.capToken ? { 'X-Evidence-Token': init.capToken } : {};

      const fd = new FormData();
      fd.append('contentType', file.type || 'image/jpeg');
      fd.append('file', file, file.name);
      const upRes = await fetch(`/v1/evidence/${encodeURIComponent(init.evidenceId)}/upload`, { method: 'POST', body: fd, headers: cap });
      const up = await upRes.json();
      if (!up.ok) throw new Error(up.message || 'Upload failed');

      const doneRes = await fetch(`/v1/evidence/${encodeURIComponent(init.evidenceId)}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...cap },
        body: JSON.stringify({ completedFrom: 'capture_react' }),
      });
      const done = await doneRes.json();
      if (!done.ok) throw new Error(done.message || 'Complete failed');

      setResult({ ok: true, message: 'Evidence uploaded!' });
      setTitle('');
      setNotes('');
      setFile(null);
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      setSamplingPoint('');
    } catch (e) {
      setResult({ ok: false, message: e.message || String(e) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Helmet><title>Capture | TERRRA</title></Helmet>
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Typography variant="h5" gutterBottom>Evidence Capture</Typography>

        {!projectId && (
          <Alert severity="info" sx={{ mb: 2 }}>Select a project from the header to start capturing.</Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={2}>

              {/* Context bar */}
              <Box sx={{ px: 1.5, py: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Project: <strong>{projectId || '—'}</strong>
                  {siteId && <> · Site: <strong>{siteId}</strong></>}
                  {fieldRecord && <> · <strong>{fieldRecord.date} · {fieldRecord.campaign_name || `#${activeFieldRecordId}`}</strong></>}
                </Typography>
              </Box>

              {/* Field record selector — shown when mode=sampling but no record pre-linked */}
              {isSamplingMode && !fieldRecordId && (
                <FormControl fullWidth size="small">
                  <InputLabel>Field Record *</InputLabel>
                  <Select
                    value={activeFieldRecordId}
                    label="Field Record *"
                    onChange={e => { setActiveFieldRecordId(e.target.value); setSamplingPoint(''); }}
                  >
                    <MenuItem value=""><em>— select field record —</em></MenuItem>
                    {availableFieldRecords.map(fr => (
                      <MenuItem key={fr.id} value={String(fr.id)}>
                        {fr.date} · {fr.campaign_name || `Record #${fr.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                  {availableFieldRecords.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      No active field records. Create one in Library → stage 6.
                    </Typography>
                  )}
                </FormControl>
              )}

              {/* Sampling point (only when mode=sampling) */}
              {isSamplingMode && (
                <FormControl fullWidth size="small">
                  <InputLabel>Sampling Point *</InputLabel>
                  <Select value={samplingPoint} label="Sampling Point *" onChange={e => setSamplingPoint(e.target.value)}>
                    <MenuItem value=""><em>— select point —</em></MenuItem>
                    {samplingPoints.map(pt => (
                      <MenuItem key={pt.name || pt.id} value={pt.name || pt.id}>
                        {pt.name || pt.id}
                        {pt.status && pt.status !== 'pending' ? ` [${pt.status}]` : ''}
                        {pt.stratum && pt.stratum !== 'Unassigned' ? ` (${pt.stratum})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingPoints && <CircularProgress size={14} sx={{ mt: 0.5 }} />}
                  {!loadingPoints && proximity && (
                    <Box sx={{
                      mt: 1, px: 1.5, py: 1, borderRadius: 1,
                      bgcolor: proximity.distM < 20 ? 'success.light' : proximity.distM < 100 ? 'warning.light' : 'grey.100',
                    }}>
                      <Typography variant="body2" fontWeight="bold">
                        {proximity.distLabel} — {proximity.direction} ({proximity.bearingDeg}°)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {proximity.distM < 5 ? '✓ You are at the point' : `Head ${proximity.direction} to reach ${samplingPoint}`}
                      </Typography>
                    </Box>
                  )}
                </FormControl>
              )}

              {/* Title */}
              <TextField
                label="Title *" size="small" fullWidth
                value={title} onChange={e => setTitle(e.target.value)}
              />

              {/* Category */}
              <FormControl fullWidth size="small">
                <InputLabel>Evidence Type</InputLabel>
                <Select value={category} label="Evidence Type" onChange={e => setCategory(e.target.value)}>
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="sampling">Sampling</MenuItem>
                  <MenuItem value="activity">Activity</MenuItem>
                </Select>
              </FormControl>

              {/* Notes */}
              <TextField
                label="Notes" size="small" fullWidth multiline rows={2}
                value={notes} onChange={e => setNotes(e.target.value)}
              />

              <Divider />

              {/* Photo buttons */}
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" fullWidth onClick={() => cameraRef.current?.click()}>
                  Take Photo
                </Button>
                <Button variant="outlined" fullWidth onClick={() => pickerRef.current?.click()}>
                  Choose File
                </Button>
              </Stack>
              <input
                ref={cameraRef} type="file" accept="image/*" capture="environment"
                style={{ display: 'none' }}
                onChange={e => { handleFileChange(e.target.files?.[0]); e.target.value = ''; }}
              />
              <input
                ref={pickerRef} type="file" accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={e => { handleFileChange(e.target.files?.[0]); e.target.value = ''; }}
              />
              {file && (
                <Typography variant="caption" color="text.secondary">
                  {file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)
                </Typography>
              )}
              {preview && (
                <Box
                  component="img" src={preview} alt="preview"
                  sx={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                />
              )}

              <Divider />

              {/* GPS status */}
              <Typography variant="caption" color={gps ? 'success.main' : 'text.secondary'}>
                📍 {gpsStatus}
              </Typography>
              {!gps && (
                <Stack direction="row" spacing={1}>
                  <TextField
                    label="Latitude" size="small" sx={{ flex: 1 }}
                    placeholder="e.g. 43.12345"
                    value={manualLat} onChange={e => setManualLat(e.target.value)}
                  />
                  <TextField
                    label="Longitude" size="small" sx={{ flex: 1 }}
                    placeholder="e.g. 11.12345"
                    value={manualLng} onChange={e => setManualLng(e.target.value)}
                  />
                </Stack>
              )}

              {/* Submit */}
              <Button
                variant="contained" size="large" fullWidth
                onClick={handleSubmit}
                disabled={submitting || !projectId}
              >
                {submitting ? <CircularProgress size={22} color="inherit" /> : 'Upload Evidence'}
              </Button>

              {result && (
                <Alert severity={result.ok ? 'success' : 'error'}>{result.message}</Alert>
              )}

            </Stack>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
